import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
// React
import React, { useEffect, useState } from 'react';

// MUI
import { Box, Button, Checkbox, createTheme, Dialog, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, Link, Stack, ThemeProvider, Typography } from '@mui/material';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';
import CheckSharpIcon from '@mui/icons-material/CheckSharp';

// Components
import { PrivacySettings } from './components/privacySettings';

// Interfaces

// Libs
import i18n from './i18n';

// Utils
import { getThemeOptions } from './utils/util';

/**
 * Renders Cookie consent modal for marketplace websites for users to give consent for the usage of cookies.
 * 
 * @param {ICookieConsent} props Input parameters for cookie consent modal.
 * @returns {JSX.Element} React Element which renders cookie consent modal.
 */
var CookieConsent = function CookieConsent(props) {
  var _props$config2, _props$config3, _props$config3$dialog, _props$config4, _props$config5;
  var theme = createTheme(props.theme ? props.theme : getThemeOptions(props.fontName));
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    marketingCookiesConsent = _useState2[0],
    setMarketingCookiesConsent = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    consentGranted = _useState4[0],
    setConsentGranted = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showModal = _useState6[0],
    setShowModal = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    showPrivacySettings = _useState8[0],
    setShowPrivacySettings = _useState8[1];
  var handleMarketingCookiesConsentChange = function handleMarketingCookiesConsentChange(event) {
    setMarketingCookiesConsent(event.target.checked);
  };
  useEffect(function () {
    i18n.init({
      lng: props.language ? props.language : 'en'
    }, function () {
      if (isValidInput()) {
        var _props$config, _props$config$dialogO;
        if ((_props$config = props.config) !== null && _props$config !== void 0 && (_props$config$dialogO = _props$config.dialogOptions) !== null && _props$config$dialogO !== void 0 && _props$config$dialogO.showPrivacySettingsDialog) {
          setShowPrivacySettings(true);
        } else {
          setShowModal(true);
        }
      } else {
        props.errorHandler(i18n.t('ERROR'));
      }
    });
  }, []);
  useEffect(function () {
    if (consentGranted && marketingCookiesConsent && showModal) {
      handleResponse();
    }
  }, [consentGranted, marketingCookiesConsent]);
  var getCookieExpiry = function getCookieExpiry(consent) {
    var date = new Date();
    var year = date.getFullYear();
    var month = date.getMonth();
    var day = date.getDate();
    return consent ? new Date(year + 1, month, day).toUTCString() : new Date(year, month + 1, day).toUTCString();
  };
  var handleResponse = function handleResponse() {
    setShowModal(false);
    props.responseHandler({
      cookies: {
        marketing: marketingCookiesConsent
      },
      expiryTime: getCookieExpiry(marketingCookiesConsent)
    });
  };
  var handleConsent = function handleConsent() {
    setConsentGranted(true);
    if (!marketingCookiesConsent) {
      setMarketingCookiesConsent(true);
    } else {
      handleResponse();
    }
  };
  var handleReject = function handleReject() {
    setShowModal(false);
    props.responseHandler({
      cookies: {
        marketing: false
      },
      expiryTime: getCookieExpiry(false)
    });
  };
  var showPrivacySettingsDialog = function showPrivacySettingsDialog() {
    setShowModal(false);
    setShowPrivacySettings(true);
  };
  var handlePrivacySettingsDialogAction = function handlePrivacySettingsDialogAction(isConsentGranted) {
    setShowPrivacySettings(false);
    props.responseHandler({
      cookies: {
        marketing: isConsentGranted
      },
      expiryTime: getCookieExpiry(isConsentGranted)
    });
  };
  var closePrivacySettingsDialog = function closePrivacySettingsDialog() {
    setShowPrivacySettings(false);
  };
  var isValidInput = function isValidInput() {
    if (!props.corporateInformation || !props.dataProtectionPolicy || !props.responseHandler || !props.errorHandler || typeof props.errorHandler !== 'function' || typeof props.responseHandler !== 'function' || props.errorHandler.length !== 1 || props.responseHandler.length !== 1) {
      return false;
    } else {
      return true;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Dialog, {
    disableEscapeKeyDown: true,
    open: props.open && showModal,
    PaperProps: {
      square: true
    },
    sx: {
      backdropFilter: 'blur(2px)',
      zIndex: 10000,
      '& .MuiDialog-paper': {
        paddingTop: '24px',
        paddingLeft: {
          lg: '24px',
          xl: '24px',
          md: '24px',
          sm: '16px',
          xs: '0'
        },
        paddingRight: {
          lg: '24px',
          xl: '24px',
          md: '24px',
          sm: '16px',
          xs: '0'
        },
        paddingBottom: '16px',
        maxWidth: '44rem',
        margin: {
          md: '32px',
          lg: '32px',
          xl: '32px',
          sm: '32px',
          xs: '0px'
        }
      }
    }
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    sx: {
      paddingTop: '8px',
      paddingBottom: '24px'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 12,
    lg: 12,
    md: 12,
    xl: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    sx: {
      fontSize: '1.5rem',
      fontWeight: 'normal',
      color: 'primary.main'
    }
  }, i18n.t('PRIVACY_SETTINGS'))))), /*#__PURE__*/React.createElement(DialogContent, {
    sx: {
      overflowY: 'visible'
    }
  }, /*#__PURE__*/React.createElement(DialogContentText, {
    component: 'div'
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    sx: {
      color: 'info.main',
      fontSize: '1rem',
      fontWeight: 'normal',
      marginBottom: '32px'
    }
  }, i18n.t('BODY_1'), "."), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    display: "block",
    sx: {
      color: 'info.main',
      fontSize: '1rem',
      fontWeight: 'normal'
    }
  }, i18n.t('BODY_2'), ':'))), /*#__PURE__*/React.createElement(Box, {
    mt: 4
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "end",
    sx: {
      color: 'info.main'
    },
    control: /*#__PURE__*/React.createElement(Checkbox, {
      sx: {
        width: '1.5rem',
        height: '1.5rem',
        padding: '0',
        margin: '9px'
      },
      icon: /*#__PURE__*/React.createElement(CheckBoxOutlineBlankSharpIcon, {
        sx: {
          backgroundColor: 'background.default',
          color: 'background.default'
        }
      }),
      checkedIcon: /*#__PURE__*/React.createElement(CheckSharpIcon, {
        sx: {
          backgroundColor: 'primary.main',
          color: 'white'
        }
      }),
      inputProps: {
        'data-testid': 'consent-checkbox'
      },
      checked: marketingCookiesConsent,
      onChange: handleMarketingCookiesConsentChange
    }),
    label: i18n.t('CONSENT'),
    labelPlacement: "end"
  }), /*#__PURE__*/React.createElement(Box, {
    mb: 3,
    sx: {
      paddingLeft: '33px',
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Link, {
    underline: "hover",
    href: props.dataProtectionPolicy
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    sx: {
      color: 'primary.main',
      fontSize: '1rem',
      fontWeight: 'normal',
      display: 'flex',
      '&::after': {
        content: '""',
        borderTop: '0.0625rem solid',
        borderRight: '0.0625rem solid',
        position: 'relative',
        display: 'inline-block',
        borderColor: 'primary.main',
        width: '0.5em',
        height: '0.5em',
        marginLeft: '0.25em',
        webkitTransform: 'translateY(0.465em) rotate(45deg)',
        transform: 'translateY(0.465em) rotate(45deg)'
      }
    }
  }, i18n.t('READ_MORE'))))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: '0.75rem',
      color: 'info.main'
    },
    variant: "body1"
  }, i18n.t('SAVE_INFO_1'), /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    sx: {
      textTransform: 'lowercase',
      textDecoration: 'underline',
      padding: '0',
      fontSize: '0.75rem',
      '&:hover': {
        background: 'none',
        textDecoration: 'underline'
      }
    },
    onClick: showPrivacySettingsDialog
  }, i18n.t('PRIVACY_SETTINGS')), i18n.t('SAVE_INFO_2'))), /*#__PURE__*/React.createElement(Box, {
    mt: 4
  }, /*#__PURE__*/React.createElement(Button, {
    sx: {
      textTransform: 'none',
      fontSize: '1rem',
      marginBottom: '1rem',
      '&:hover': {
        boxShadow: 'none'
      }
    },
    "data-testid": "consent-button",
    color: "primary",
    fullWidth: true,
    disableElevation: true,
    variant: "contained",
    onClick: handleConsent
  }, i18n.t('CONSENT')), ((_props$config2 = props.config) === null || _props$config2 === void 0 ? void 0 : _props$config2.showRejectButton) && /*#__PURE__*/React.createElement(Button, {
    sx: {
      textTransform: 'none',
      fontSize: '1rem',
      marginBottom: '1.5rem',
      '&:hover': {
        boxShadow: 'none'
      }
    },
    color: "primary",
    "data-testid": "reject-button",
    fullWidth: true,
    disableElevation: true,
    variant: "outlined",
    onClick: handleReject
  }, i18n.t('REJECT')), /*#__PURE__*/React.createElement(Button, {
    sx: {
      textTransform: 'none',
      fontSize: '1rem',
      '&:hover': {
        backgroundColor: 'none'
      }
    },
    color: "primary",
    fullWidth: true,
    variant: "text",
    onClick: function onClick() {
      return handleResponse();
    }
  }, i18n.t('SAVE'))), /*#__PURE__*/React.createElement(Stack, {
    sx: {
      textAlign: {
        md: 'left',
        lg: 'left',
        xl: 'left',
        sm: 'center',
        xs: 'center'
      }
    },
    direction: {
      xs: 'column',
      sm: 'column',
      md: 'row',
      lg: 'row',
      xl: 'row'
    },
    spacing: 2,
    mt: 5
  }, /*#__PURE__*/React.createElement(Link, {
    href: props.dataProtectionPolicy,
    underline: "hover"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    component: 'span',
    sx: {
      color: 'primary.main',
      fontSize: {
        md: '1rem',
        lg: '1rem',
        xl: '1rem',
        sm: '0.8rem',
        xs: '0.8rem'
      }
    }
  }, i18n.t('DATA_PROTECTION_NOTICE')), "\xA0"), /*#__PURE__*/React.createElement(Link, {
    href: props.corporateInformation,
    underline: "hover"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    component: 'span',
    sx: {
      color: 'primary.main',
      fontSize: {
        md: '1rem',
        lg: '1rem',
        xl: '1rem',
        sm: '0.8rem',
        xs: '0.8rem'
      }
    }
  }, i18n.t('CORPORATE_INFORMATION')))))), /*#__PURE__*/React.createElement(PrivacySettings, {
    open: showPrivacySettings,
    dataProtectionPolicy: props.dataProtectionPolicy,
    corporateInformation: props.corporateInformation,
    responseHandler: handleConsent,
    cookies: {
      marketing: (_props$config3 = props.config) !== null && _props$config3 !== void 0 && (_props$config3$dialog = _props$config3.dialogOptions) !== null && _props$config3$dialog !== void 0 && _props$config3$dialog.showPrivacySettingsDialog ? (_props$config4 = props.config) === null || _props$config4 === void 0 ? void 0 : _props$config4.dialogOptions.cookies.marketing : false
    },
    showOnlyPrivacySettingsDialog: (_props$config5 = props.config) !== null && _props$config5 !== void 0 && _props$config5.dialogOptions ? props.config.dialogOptions.showPrivacySettingsDialog : false,
    handlePrivacySettingsDialogAction: handlePrivacySettingsDialogAction,
    closePrivacySettingsDialog: closePrivacySettingsDialog,
    exitHandler: props.exitHandler
  })));
};
export { CookieConsent };